
import {
  computed, defineComponent, onBeforeMount, onMounted, reactive,
} from 'vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioMatematico from '@/core/utilitarios/UtilitarioMatematico';
import { useTelaBase } from '@/core/composables/TelaBase';
import SelecionarColaborador from '@/components/Cadastros/Pessoas/Colaboradores/SelecionarColaborador.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import storeSistema from '@/store/storeSistema';
import Icone from '@/core/components/Icone.vue';
import { IDevolucaoVendaComissao } from '@/models/Entidades/Vendas/IDevolucaoVenda';

export default defineComponent({
  name: 'DevolucaoVendaComissao',
  props: {
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    valorVenda: {
      type: Number,
      required: true,
    },
    comissoes: {
      type: Array as () => IDevolucaoVendaComissao[],
      required: true,
    },
    editar: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    SelecionarColaborador,
    CampoNumerico,
    Icone,
  },
  emits: ['update:comissoes', 'recalcularComissao'],
  setup(props, { emit }) {
    const { apresentarMensagemAlerta } = useTelaBase();

    const state = reactive({
      colunasGrade: [] as IColumn[],
      indexSelecionado: -1,
      colaboradorSelecionado: 0,
      nomeSelecionado: '',
      comissaoSelecionada: 0,
      observacao: '',
    });

    const computedComissoes = computed({
      get: () => props.comissoes,
      set: (val: IDevolucaoVendaComissao[]) => {
        emit('update:comissoes', val);
      },
    });

    function preencherColunas() {
      state.colunasGrade = [
        {
          title: 'Codigo', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
        },
        {
          title: 'Colaborador', dataIndex: 'nomeColaborador', key: 'Colaborador', position: 2, visible: true, align: 'left', width: 450,
        },
        {
          title: '% Base', dataIndex: 'percentualBase', key: 'PercentualBase', position: 3, visible: true, align: 'right', width: 100, customRenderRow: ECustomRenderRow.MascararPorcentagem,
        },
        {
          title: '% Decréscimo', dataIndex: 'percentualDecrescimo', key: 'PercentualDecrescimo', position: 3, visible: true, align: 'right', width: 100, customRenderRow: ECustomRenderRow.MascararPorcentagem,
        },
        {
          title: '% Comissão', dataIndex: 'percentualComissao', key: 'PercentualComissao', position: 3, visible: true, align: 'right', width: 100, customRenderRow: ECustomRenderRow.MascararPorcentagem,
        },
        {
          title: 'Vrl Comissão', dataIndex: 'valorComissao', key: 'Comissao', position: 3, visible: true, align: 'right', width: 100, customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Alterado por', dataIndex: 'nomeUsuarioAlteracao', key: 'Usuario', position: 4, visible: true, align: 'left', width: 200,
        },
        {
          title: 'Observação', dataIndex: 'observacao', key: 'Observacao', position: 5, visible: true, align: 'left',
        },
        {
          title: 'Ações', key: 'acoes', position: 6, visible: true, width: 100, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function inserirComissao() {
      if (state.colaboradorSelecionado === 0) {
        apresentarMensagemAlerta('O colaborador deve ser informado!');
        return;
      }

      if (state.indexSelecionado > -1) {
        computedComissoes.value[state.indexSelecionado].codigoColaborador = state.colaboradorSelecionado;
        computedComissoes.value[state.indexSelecionado].nomeColaborador = state.nomeSelecionado;
        computedComissoes.value[state.indexSelecionado].percentualBase = state.comissaoSelecionada;
        computedComissoes.value[state.indexSelecionado].percentualDecrescimo = 0;
        computedComissoes.value[state.indexSelecionado].percentualComissao = state.comissaoSelecionada;
        computedComissoes.value[state.indexSelecionado].valorComissao = UtilitarioMatematico.calcularValorPercentual(props.valorVenda, state.comissaoSelecionada);
        computedComissoes.value[state.indexSelecionado].observacao = state.observacao;
        computedComissoes.value[state.indexSelecionado].codigoUsuarioAlteracao = storeSistema.getters.codigoUsuarioAutenticado();
        computedComissoes.value[state.indexSelecionado].nomeUsuarioAlteracao = storeSistema.getters.nomeUsuarioAutenticado();
      } else {
        const comissao = {} as IDevolucaoVendaComissao;
        comissao.codigoColaborador = state.colaboradorSelecionado;
        comissao.codigoTituloFinanceiro = 0;
        comissao.nomeColaborador = state.nomeSelecionado;
        comissao.percentualBase = state.comissaoSelecionada;
        comissao.percentualDecrescimo = 0;
        comissao.percentualComissao = state.comissaoSelecionada;
        comissao.valorComissao = UtilitarioMatematico.calcularValorPercentual(props.valorVenda, comissao.percentualComissao);
        comissao.observacao = state.observacao;
        comissao.codigoUsuarioAlteracao = storeSistema.getters.codigoUsuarioAutenticado();
        comissao.nomeUsuarioAlteracao = storeSistema.getters.nomeUsuarioAutenticado();
        computedComissoes.value.push(comissao);
      }
      state.indexSelecionado = -1;
      state.colaboradorSelecionado = 0;
      state.nomeSelecionado = '';
      state.comissaoSelecionada = 0;
    }

    function editarComissao(index: number) {
      state.indexSelecionado = index;
      state.colaboradorSelecionado = computedComissoes.value[index].codigoColaborador;
      state.nomeSelecionado = computedComissoes.value[index].nomeColaborador;
      state.comissaoSelecionada = computedComissoes.value[index].percentualBase;
    }

    function excluirComissao(index: number) {
      state.indexSelecionado = -1;
      state.colaboradorSelecionado = 0;
      state.nomeSelecionado = '';
      state.comissaoSelecionada = 0;
      computedComissoes.value.splice(index, 1);
      computedComissoes.value.forEach((c, i) => {
        if (!UtilitarioGeral.validaCodigo(computedComissoes.value[i].codigoUsuarioAlteracao)) {
          computedComissoes.value[i].codigoUsuarioAlteracao = storeSistema.getters.codigoUsuarioAutenticado();
          computedComissoes.value[i].nomeUsuarioAlteracao = storeSistema.getters.nomeUsuarioAutenticado();
        }
      });
    }

    function recalcularComissao() {
      emit('recalcularComissao', true);
    }

    onMounted(() => {
      // Se a lista estiver vazia e houver valor de venda, pede para recalcular automaticamente
      if (props.comissoes.length === 0 && props.valorVenda > 0) {
        emit('recalcularComissao', true);
      }
    });

    onBeforeMount(async () => {
      preencherColunas();
    });

    return {
      props,
      state,
      ECustomRenderRow,
      computedComissoes,
      UtilitarioMascara,
      UtilitarioMatematico,
      UtilitarioGeral,
      inserirComissao,
      editarComissao,
      excluirComissao,
      recalcularComissao,
    };
  },
});
